/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useCallback, useMemo } from "react"
import {
  useLocation,
  useNavigate,
  matchPath,
  useSearchParams,
} from "react-router-dom"
import { Box, useTheme } from "@mui/material"
import _ from "lodash"

import config from "../config"

import { useVenue } from "providers/venue"
import { useGeoLocation } from "providers/geolocation"
import { useAppUI } from "../providers/ui"
import { useMapViewConfig } from "hooks/map/useMapViewConfig"
import {
  getFeatureByLocationId,
  getFeaturesByLocationId,
  getLocationIdByFeature,
  getOrdinalByLocationId,
  getRelatedLocationIdsByFeature,
  getSectionByFeature,
  getSuitablyValueBetweenBearings,
  isClickableFeature,
  parseElementId,
  BasemapAttribution,
} from "components/IndoorMap"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"
import { useLimitMapView } from "providers/venue/modules/indoormap/hooks/useLimitMapView"

const FALLBACK_CENTER = [100.5017051, 13.7572619]
const DEFAULT_ZOOM = 18.5

export const IndoorMapContainer = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isLoading, userLocation, scannedLocations, deviceLocation } =
    useGeoLocation()

  const {
    venues,
    features,
    occupants,
    amenities,
    labels,
    decorations,
    dataLoaded,
    relationshipGraphLoaded,
    mapTheme,
    mapConfig,
  } = useVenue()
  const mapViewConfig = useMapViewConfig()
  const { indoorRef, mapLoaded, mapLoading, mapCreated } = useIndoorMap()
  const { mapReady, mapLayout, setMapLayout } = useAppUI()
  const theme = useTheme()
  const styleOverrides = theme.components[`AppMap`]?.styleOverrides || {}
  const { indoorMapContainer } = styleOverrides

  const defaultCenter = useMemo(
    () =>
      _.get(venues, "[0].properties.display_point.coordinates") ||
      FALLBACK_CENTER,
    [venues]
  )

  const isKioskMode = useMemo(
    () => ["kiosk-v", "kiosk-h"].includes(config("device")),
    []
  )

  const extentScaleMapViewFactor = useMemo(
    () => _.get(mapConfig, "extent_scale_map_view_factor"),
    [mapConfig]
  )

  useLimitMapView(venues, {
    scale: extentScaleMapViewFactor,
  })

  useEffect(() => {
    /* Handle User location
     * @Eample: /maps/ordinal/2?user-location=98.29877730421258,7.892412617738349,2o
     */
    if (!indoorRef.current) return
    if (mapLoaded && !mapLoading && isKioskMode && deviceLocation) {
      indoorRef.current?.addUserLocation(deviceLocation)
    } else if (mapLoaded && !mapLoading && userLocation) {
      indoorRef.current?.addUserLocation(userLocation)
    } else {
      indoorRef.current?.removeUserLocation()
    }
  }, [userLocation, mapLoaded, mapLoading, deviceLocation, indoorRef.current])

  useEffect(() => {
    /**
     * Handle last scanned locations
     */
    if (!indoorRef.current) return

    //TODO:: make this logic supports multiple locations?
    const scannedLocation = _.last(scannedLocations)
    if (mapReady && !mapLoading && scannedLocation) {
      indoorRef.current?.removeLastUserLocation()
      indoorRef.current?.addLastUserLocation(scannedLocation)
    } else {
      indoorRef.current?.removeLastUserLocation()
    }
  }, [
    scannedLocations,
    mapReady,
    mapLoading,
    deviceLocation,
    indoorRef.current,
  ])

  useEffect(() => {
    if (
      !indoorRef.current ||
      !mapLoaded ||
      !dataLoaded ||
      !mapTheme ||
      !relationshipGraphLoaded
    )
      return

    if (location.pathname !== "/" && location.pathname.indexOf("/maps") !== 0) {
      // Hide Map
      setMapLayout("hidden")
      return
    }

    const [action] = location.pathname.replace("/maps/", "").split("/")

    setMapLayout("full")

    // Handle the map's direction and the map's interactive
    switch (action) {
      case "ordinal":
      case "place":
      case "dir":
        break
      case "":
        indoorRef.current.clearNavigationGeometries()
        indoorRef.current.disableClick()
        break
      default:
        indoorRef.current.clearNavigationGeometries()
        indoorRef.current.enableClick()
        indoorRef.current.setFeatureObject3DsOpacity(1)
        break
    }

    // Handle the map's rendering
    switch (action) {
      case "place":
      case "ordinal":
      case "dir":
      case "occupants":
      case "zone":
      case "openings":
      case "amenities": {
        break
      }
      case "": {
        setMapLayout("bottom-half")
        break
      }
      default: {
        indoorRef.current.showVenueObjects()
        indoorRef.current.changeLevelByOrdinal(0)
        const currentBearing = indoorRef.current.getBearing()
        const bearing = getSuitablyValueBetweenBearings(0, currentBearing)
        indoorRef.current.flyTo(defaultCenter, {
          zoom: DEFAULT_ZOOM,
          bearing,
        })
        indoorRef.current.clearHighlightElements()
        indoorRef.current.clearHighlightObject()
        break
      }
    }
  }, [
    indoorRef,
    location.pathname,
    isLoading,
    relationshipGraphLoaded,
    mapLoaded,
  ])

  useEffect(() => {
    // If the user enters `/maps`, navigate to the ordinal with the user's location; otherwise, navigate to the ground floor (ordinal 0) and use `replace` to prevent returning to `/maps`.
    const isQuickStartMapPath = matchPath("/maps", location.pathname)
    if (isQuickStartMapPath) {
      const initialOrdinal = _.get(userLocation, "properties.ordinal", 0)
      navigate(`/maps/ordinal/${initialOrdinal}?${searchParams.toString()}`, {
        replace: true,
      })
    }
  }, [navigate, searchParams])

  const handleClickElement = useCallback(
    (params) => {
      const replace = matchPath("/maps/place/:placeId", location.pathname)
      const sectionFeatureFlag = config("sectionFeatureFlag")

      const {
        id: elementId,
        feature_type,
        category,
      } = params.target.properties || params.target.options.properties

      const id = parseElementId(elementId)

      if (
        feature_type === "occupant" ||
        (sectionFeatureFlag && feature_type === "section")
      )
        return navigate(`/maps/place/${id}`, { replace })

      switch (`${feature_type}-${category}`) {
        case "unit-room":
        case "unit-restroom":
        case "unit-stairs":
        case "unit-escalator":
        case "unit-elevator":
        case "unit-firstaid":
        case "unit-unenclosedarea":
        case "unit-unspecified":
        case "unit-terrace":
        case "unit-vegetation":
        case "unit-parking":
        case "unit-recreation":
        case "unit-fieldofplay":
        case "kiosk-kiosk":
          // Find occupant ID or amenity ID using the location ID from a unit or kiosk.
          const featureToNavigate =
            getFeatureByLocationId(id, occupants) ||
            getFeatureByLocationId(id, amenities)

          /**
           * If the clicked element is within a section and has multiple bound occupants,
           * navigate to the section Place Detail instead of the occupant
           */
          const section = getSectionByFeature(featureToNavigate)
          const sectionId = section?.id
          if (sectionId && sectionFeatureFlag) {
            const hasOccupants =
              getFeaturesByLocationId(id, occupants).length > 1
            if (hasOccupants) {
              navigate(`/maps/place/${sectionId}`, { replace })
              break
            }
          }

          const canNavigate = isClickableFeature(featureToNavigate)
          if (featureToNavigate && canNavigate) {
            const mainLocationId = getLocationIdByFeature(featureToNavigate)
            // Only check related locations if clicked ID is different from main ID and not the same ordinal
            const needsUnitQuery =
              id !== mainLocationId &&
              getRelatedLocationIdsByFeature(featureToNavigate).includes(id) &&
              getOrdinalByLocationId(id, featureToNavigate) !==
                getOrdinalByLocationId(mainLocationId, featureToNavigate)
            navigate(
              `/maps/place/${featureToNavigate.id}${
                needsUnitQuery ? `?target=${id}` : ""
              }`,
              { replace }
            )
          }
          break
        case "amenity-atm":
        case "amenity-babychanging":
        case "amenity-strollerrental":
        case "amenity-boardinggate.ferry":
        case "amenity-elevator":
        case "amenity-escalator":
        case "amenity-stairs":
        case "amenity-information":
        case "amenity-information.transit":
        case "amenity-wheelchair":
        case "amenity-restroom":
        case "amenity-restroom.male":
        case "amenity-restroom.female":
        case "amenity-restroom.wheelchair":
        case "amenity-restroom.family":
        case "amenity-restroom.unisex":
        case "amenity-taxi":
        case "amenity-groundtransportation":
        case "amenity-bus":
        case "amenity-bus.muni":
        case "amenity-shuttle":
        case "amenity-parking":
        case "amenity-parking.bicycle":
        case "amenity-parking.motorcycle":
        case "amenity-parking.shortterm":
        case "amenity-parking.longterm":
        case "amenity-parking.waitingarea":
        case "amenity-parking.compact":
        case "amenity-parking.ev":
        case "amenity-privatelounge":
        case "amenity-rideshare":
        case "amenity-valet":
        case "amenity-landmark":
        case "amenity-rail.muni":
        case "amenity-service":
        case "amenity-smokingarea":
        case "amenity-ticketing":
        case "amenity-meetingpoint":
        case "amenity-prayerroom":
        case "amenity-firstaid":
        case "amenity-ticketing.rail":
        case "amenity-exhibit":
        case "amenity-mothersroom":
        case "amenity-checkin.desk":
        case "amenity-baggagestorage":
        case "amenity-baggagecarts":
        case "amenity-powerchargingstation":
        case "amenity-wheelchairassit":
        case "amenity-coinlocker":
        case "amenity-library":
        case "amenity-lostandfound":
        case "amenity-foodservice":
        case "amenity-businesscenter":
        case "amenity-guestservices":
        case "amenity-emergencyshelter":
        case "amenity-prayerroom.buddhism":
        case "amenity-defibrillator":
        case "amenity-drinkingfountain":
        case "amenity-handsanitizerstation":
        case "amenity-unspecified":
        case "amenity-entry":
        case "amenity-fireextinguisher":
        case "amenity-security":
        case "amenity-seating":
        case "amenity-copymachine":
        case "amenity-fieldofplay":
        case "amenity-fieldofplay.soccer":
        case "amenity-amphitheater":
        case "amenity-hoteling":
        case "occupant-currencyexchange":
        case "occupant-donationcenter":
        case "occupant-bank":
        case "occupant-books":
        case "occupant-postoffice":
        case "opening-emergencyexit":
          navigate(`/maps/place/${id}`, { replace })
          break
        default:
          break
      }
    },
    /** Remove navigate because it trigger unneccessary re-render & createFeatures in Indoormap */
    [occupants, navigate, location]
  )

  useEffect(() => {
    if (indoorRef.current) {
      indoorRef.current.onClickElement = handleClickElement
    }
  }, [indoorRef, dataLoaded, mapCreated, handleClickElement])

  /** Init with features  */
  useEffect(() => {
    if (
      indoorRef.current &&
      mapCreated &&
      // !indoorRef.current.elementsLoaded &&
      mapTheme &&
      dataLoaded
    ) {
      indoorRef.current.mapDecorations = decorations
      indoorRef.current.features = features
      indoorRef.current.groundLabels = labels
      indoorRef.current.mapTheme = mapTheme
      indoorRef.current.defaultBearing = mapViewConfig.bearing
      indoorRef.current.defaultPitch = mapViewConfig.pitch
      indoorRef.current.defaultZoom = mapViewConfig.zoom
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    indoorRef.current,
    features,
    mapTheme,
    labels,
    decorations,
    dataLoaded,
    mapCreated,
    mapViewConfig,
  ])

  return (
    <Box sx={indoorMapContainer} className={mapLayout}>
      <div id="map"></div>
      <BasemapAttribution basemap={_.get(mapConfig, "basemap")} />
    </Box>
  )
}
